import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { apiHandler } from "../../assets/api";
import { endpoint } from "../../assets/api/endpoint";
import { toast } from "react-toastify";
import CustomDatePicker from "../../components/CustomDatePicker";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CustomButton from "../../components/CustomButton";
import { format } from "date-fns";
import CustomInput from "../../components/CustomInput";

const Callback = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [showDobForm, setShowDobForm] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [dob, setDob] = useState(null);
  const [guardianEmail, setGuardianEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [isProcessingCallback, setIsProcessingCallback] = useState(true);

  useEffect(() => {
    const processOAuthCallback = async () => {
      try {
        const currentUrl = window.location.href;
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");
        const state = urlParams.get("state");
        // Check if coming from Google OAuth callback
        if (currentUrl.includes("api.itengo.pro/login/oauth2/code/google") && code) {
          const response = await fetch(`/login/oauth2/code/google${window.location.search}`);
          const userData = await response.json();

          if (userData?.token && userData?.email) {
            localStorage.setItem("authToken", userData.token);
            localStorage.setItem("userInfo", JSON.stringify(userData));
            setUserInfo(userData);

            if (!userData.isOnboarded) {
              setShowDobForm(true);
            } else {
              toast.success("Login successful!");
              navigate("/dashboard");
            }
          } else {
            throw new Error("Invalid OAuth response");
          }
        } else {
          // fallback for redirect with token + email
          const token = params.get("token");
          const email = params.get("email");

          if (token && email) {
            localStorage.setItem("authToken", token);
            setShowDobForm(true);
          } else {
            throw new Error("Missing OAuth parameters");
          }
        }
      } catch (error) {
        console.error("OAuth callback error:", error);
        toast.error("Authentication failed. Please sign in again.");
        navigate("/login", { replace: true, state: { from: "oauth-failed" } });
      } finally {
        setIsProcessingCallback(false);
      }
    };

    processOAuthCallback();
  }, [params, navigate]);

  const getAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleDOBChange = (date) => {
    setDob(date);

    if (!date) {
      setErrors((prev) => ({ ...prev, dob: "Date of Birth is required" }));
    } else {
      const age = getAge(date);
      if (age < 13) {
        setErrors((prev) => ({ ...prev, dob: "You must be at least 13 years old" }));
      } else {
        setErrors((prev) => ({ ...prev, dob: null }));
      }
    }
  };

  const validateGuardianEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async () => {
    setError("");
    setErrors({});

    if (!dob) {
      setErrors({ dob: "Please enter your date of birth." });
      return;
    }

    const age = getAge(dob);
    if (age < 13) {
      setError("You must be at least 13 years old to proceed.");
      return;
    }

    if (age < 18 && !guardianEmail) {
      setErrors({ guardianEmail: "Guardian's email is required for minors under 18." });
      return;
    }

    if (age < 18 && !validateGuardianEmail(guardianEmail)) {
      setErrors({ guardianEmail: "Please enter a valid guardian email address." });
      return;
    }

    try {
      setLoading(true);
      const userData = userInfo || JSON.parse(localStorage.getItem("userInfo") || "{}");

      const result = await apiHandler({
        url: endpoint.OUTH_SIGN_UP,
        method: "POST",
        data: {
          name: userData.name || "User",
          email: userData.email,
          dateOfBirth: format(dob, "yyyy-MM-dd"),
          guardianEmail: age < 18 ? guardianEmail : null,
          mobileNumber: userData.mobileNumber || null,
          checked: true,
          longitude: "-74.3149733",
          latitude: "40.47635706",
        },
      });

      if (result?.data?.accessToken || result?.success) {
        toast.success("Registration completed successfully!");
        localStorage.setItem("authToken", result?.data?.accessToken || userData.token);
        navigate("/dashboard");
      } else {
        toast.error(result?.data?.message || result?.message || "Registration failed");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error?.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGuardianEmailBlur = (e) => {
    const { value } = e.target;
    if (value && !validateGuardianEmail(value)) {
      setErrors((prev) => ({ ...prev, guardianEmail: "Please enter a valid email address" }));
    } else {
      setErrors((prev) => ({ ...prev, guardianEmail: null }));
    }
  };

  const age = dob ? getAge(dob) : null;

  if (isProcessingCallback) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <div>Processing authentication...</div>
      </div>
    );
  }

  if (showDobForm) {
    return (
      <div className="d-flex justify-content-center" style={{ padding: "20px", height: "80vh" }}>
        <div
          style={{
            margin: "20px 0",
            padding: "20px",
            border: "1px solid #ccc",
            borderRadius: "10px",
            backgroundColor: "white",
            width: "50%",
            boxShadow: "rgba(96, 97, 112, 0.16) 0px 2px 4px 0px",
          }}
        >
          <h4 className="mt-3">Complete Your Registration</h4>
          <p className="text-muted">Please provide your date of birth to continue</p>

          <div className="mt-4">
            <DatePicker
              selected={dob}
              onChange={handleDOBChange}
              customInput={
                <CustomDatePicker
                  isInvalid={!!errors.dob}
                  placehold="Date Of Birth"
                  label="Date Of Birth"
                  value={dob ? format(dob, "MMMM d, yyyy") : ""}
                  style={{ width: "100%" }}
                  className={errors.dob ? "is-invalid " : ""}
                />
              }
              dateFormat="MMMM d, yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              maxDate={new Date()}
            />
            {errors.dob && <div className="text-danger mt-1">{errors.dob}</div>}
          </div>

          {age >= 13 && age < 18 && (
            <div className="mt-3">
              <CustomInput
                name="guardianEmail"
                label="Guardian's Email Address"
                placeholder="Enter guardian's email address"
                type="email"
                value={guardianEmail}
                className={errors.guardianEmail ? "input-error" : ""}
                onChange={(e) => setGuardianEmail(e.target.value)}
                onBlur={handleGuardianEmailBlur}
                required
              />
              {errors.guardianEmail && <div className="text-danger mt-1">{errors.guardianEmail}</div>}
              <small className="text-muted">
                Since you're under 18, we need your guardian's email for verification.
              </small>
            </div>
          )}

          {error && <div className="alert alert-danger mt-3">{error}</div>}

          <div className="mt-4">
            <CustomButton
              buttonText={loading ? "Submitting..." : "Complete Registration"}
              className="border"
              style={{
                width: "100%",
                height: "48px",
                borderRadius: "12px",
                background: "#006FFD",
                color: "#FFFFFF",
              }}
              onClick={handleSubmit}
              disabled={loading}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
      <p>Redirecting to login...</p>
    </div>
  );
};

export default Callback;
